import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Camera, Trash2, Loader2 } from "lucide-react";
import { useAccount, photoUrl, errMsg } from "@/lib/account";
import { ALL_COUNTRIES } from "@/lib/countries";

const EMPTY = { name: "", gender: "Female", age: 25, religion: "Hindu", mother_tongue: "", city: "", country: "India", profession: "", education: "", height: "", diet: "Vegetarian", interests: "", about: "", chat_requires_interest: true, open_to_international: true };

const EmailAlerts = () => {
  const { account, setAccount, authed } = useAccount();
  const on = account?.email_alerts !== false;
  const toggle = async () => {
    try { const r = await authed("put", "/me/alerts", { enabled: !on }); setAccount((a) => ({ ...a, email_alerts: r.email_alerts })); toast.success(r.email_alerts ? "Email alerts on" : "Email alerts off"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="card-royal rounded-2xl p-6 flex items-center justify-between gap-4" data-testid="email-alerts-section">
      <div><h2 className="font-display text-2xl">Email alerts</h2><p className="text-sm text-slate-400 mt-1">Get emails for new interests and messages, payment receipts, and 5 hand-picked matches every Sunday.</p></div>
      <button onClick={toggle} data-testid="email-alerts-toggle" className={`${on ? "btn-gold" : "btn-ghost"} px-5 py-2 rounded-full text-sm shrink-0`}>{on ? "On" : "Off"}</button>
    </div>
  );
};

const Field = ({ label, children }) => (<label className="block"><span className="text-xs uppercase tracking-widest text-slate-400">{label}</span><div className="mt-2">{children}</div></label>);

const Photos = ({ profile }) => {
  const { authed, setProfile } = useAccount();
  const [busy, setBusy] = useState(false);
  const ref = useRef();
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    setBusy(true);
    try { setProfile(await authed("post", "/me/photos", fd)); toast.success("Photo added"); }
    catch (err) { toast.error(errMsg(err)); }
    finally { setBusy(false); e.target.value = ""; }
  };
  const remove = async (url) => {
    try { setProfile(await authed("post", "/me/photos/delete", { url })); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <div className="card-royal rounded-2xl p-6" data-testid="photos-section">
      <h2 className="font-display text-2xl">Your photographs</h2>
      <p className="text-sm text-slate-400 mt-1">The first photo is shown on your card. JPG, PNG or WEBP up to 8 MB.</p>
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 mt-5">
        {profile.photos.map((u, i) => (
          <div key={u} className="relative aspect-square rounded-xl overflow-hidden group" data-testid={`my-photo-${i}`}>
            <img src={photoUrl(u)} alt="" className="w-full h-full object-cover" />
            <button onClick={() => remove(u)} data-testid={`my-photo-delete-${i}`} className="absolute top-2 right-2 p-1.5 rounded-full bg-black/70 text-red-300 opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
        <button onClick={() => ref.current.click()} disabled={busy} data-testid="photo-upload-button" className="aspect-square rounded-xl border-2 border-dashed border-amber-500/40 flex flex-col items-center justify-center text-amber-300 hover:bg-amber-500/5 transition-colors">
          {busy ? <Loader2 className="w-6 h-6 animate-spin" /> : <Camera className="w-6 h-6" />}<span className="text-xs mt-2">{busy ? "Uploading" : "Add photo"}</span>
        </button>
        <input ref={ref} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={upload} data-testid="photo-upload-input" />
      </div>
    </div>
  );
};

export default function MyProfile() {
  const { profile, setProfile, authed, loading } = useAccount();
  const [f, setF] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (profile) setF({ ...EMPTY, ...profile, interests: (profile.interests || []).join(", ") }); }, [profile]);
  const on = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = { ...f, age: Number(f.age), interests: f.interests.split(",").map((s) => s.trim()).filter(Boolean).slice(0, 10) };
      ["id", "user_id", "photos", "is_ai", "created_at", "updated_at", "reviewed", "reviewed_at", "boost", "boost_until"].forEach((k) => delete body[k]);
      setProfile(await authed("put", "/me/profile", body));
      toast.success("Profile saved");
    } catch (err) { toast.error(errMsg(err)); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="py-32 text-center text-slate-500">Loading...</div>;
  const inp = (k, props = {}) => <input className="field" value={f[k]} onChange={on(k)} data-testid={`profile-${k}-input`} {...props} />;

  return (
    <div className="max-w-4xl mx-auto px-5 lg:px-8 py-14 space-y-8" data-testid="my-profile-page">
      <div>
        <p className="text-xs uppercase tracking-[0.25em] text-amber-400 font-semibold">{profile ? "Your profile" : "Begin your journey"}</p>
        <h1 className="font-display text-4xl sm:text-5xl font-semibold mt-3">{profile ? "Edit your profile" : "Create your profile"}</h1>
      </div>
      <form onSubmit={save} className="card-royal rounded-2xl p-6 grid sm:grid-cols-2 gap-5" data-testid="profile-form">
        <Field label="Full name">{inp("name", { required: true, minLength: 2 })}</Field>
        <Field label="I am a">
          <select className="field" value={f.gender} onChange={on("gender")} data-testid="profile-gender-select"><option value="Female">Bride (Female)</option><option value="Male">Groom (Male)</option></select>
        </Field>
        <Field label="Age">{inp("age", { type: "number", min: 18, max: 80, required: true })}</Field>
        <Field label="Religion">
          <select className="field" value={f.religion} onChange={on("religion")} data-testid="profile-religion-select">{["Hindu", "Muslim", "Christian", "Sikh", "Jain", "Buddhist", "Jewish", "Non-religious", "Spiritual", "Other"].map((r) => <option key={r}>{r}</option>)}</select>
        </Field>
        <Field label="Country">
          <select className="field" value={f.country} onChange={on("country")} data-testid="profile-country-select">{ALL_COUNTRIES.map((c) => <option key={c}>{c}</option>)}</select>
        </Field>
        <Field label="Mother tongue">{inp("mother_tongue", { required: true })}</Field>
        <Field label="City">{inp("city", { required: true })}</Field>
        <Field label="Profession">{inp("profession", { required: true })}</Field>
        <Field label="Education">{inp("education", { required: true })}</Field>
        <Field label="Height">{inp("height", { placeholder: "5'6\"" })}</Field>
        <Field label="Diet">
          <select className="field" value={f.diet} onChange={on("diet")} data-testid="profile-diet-select">{["Vegetarian", "Non-Vegetarian", "Eggetarian", "Vegan"].map((r) => <option key={r}>{r}</option>)}</select>
        </Field>
        <div className="sm:col-span-2"><Field label="Interests (comma separated)">{inp("interests", { placeholder: "travel, music, cooking" })}</Field></div>
        <div className="sm:col-span-2"><Field label="About me">
          <textarea className="field min-h-[120px]" value={f.about} onChange={on("about")} maxLength={1500} data-testid="profile-about-input" placeholder="Share a little about yourself and your family" />
        </Field></div>
        <label className="sm:col-span-2 flex items-center gap-3 text-sm text-slate-300 cursor-pointer">
          <input type="checkbox" checked={f.chat_requires_interest} onChange={(e) => setF((s) => ({ ...s, chat_requires_interest: e.target.checked }))} className="w-4 h-4 accent-amber-500" data-testid="profile-require-interest-checkbox" />
          Only members whose interest I accept can message me
        </label>
        <label className="sm:col-span-2 flex items-center gap-3 text-sm text-slate-300 cursor-pointer">
          <input type="checkbox" checked={f.open_to_international} onChange={(e) => setF((s) => ({ ...s, open_to_international: e.target.checked }))} className="w-4 h-4 accent-amber-500" data-testid="profile-international-checkbox" />
          I'm open to a partner from another country, culture or ethnicity
        </label>
        <div className="sm:col-span-2"><button type="submit" disabled={saving} data-testid="profile-save-button" className="btn-gold px-8 py-3 rounded-full font-medium">{saving ? "Saving..." : "Save profile"}</button></div>
      </form>
      <EmailAlerts />
      {profile && <Photos profile={profile} />}
    </div>
  );
}
